export interface HexdumpOptions {
  bytesPerLine: number;
  uppercase: boolean;
  offsetBase: 10 | 16;
}

export type ByteColorGroup =
  | '00'
  | '0'
  | '1'
  | '2'
  | '3'
  | '4'
  | '5'
  | '6'
  | '7'
  | '8'
  | '9'
  | 'a'
  | 'b'
  | 'c'
  | 'd'
  | 'e'
  | 'f'
  | 'ff';

export interface HexdumpByte {
  offset: number;
  value: number;
  hex: string;
  ascii: string;
  colorGroup: ByteColorGroup;
}

export interface HexdumpLine {
  startOffset: number;
  offset: string;
  bytes: HexdumpByte[];
}

const PRINTABLE_MIN = 32;
const PRINTABLE_MAX = 126;

export function getByteColorGroup(byte: number): ByteColorGroup {
  if (byte === 0) {
    return '00';
  }
  if (byte === 0xff) {
    return 'ff';
  }
  return Math.floor(byte / 16).toString(16) as ByteColorGroup;
}

export function formatHexdumpLines(input: Uint8Array, options: HexdumpOptions): HexdumpLine[] {
  const rowCount = getHexdumpRowCount(input.length, options.bytesPerLine);
  return Array.from({ length: rowCount }, (_, rowIndex) =>
    formatHexdumpLine(input, rowIndex, options),
  );
}

export function getHexdumpRowCount(byteLength: number, bytesPerLine: number): number {
  if (!Number.isInteger(bytesPerLine) || bytesPerLine <= 0) {
    throw new Error('Bytes per line must be a positive integer');
  }
  return Math.ceil(byteLength / bytesPerLine);
}

export function formatHexdumpLine(
  input: Uint8Array,
  rowIndex: number,
  options: HexdumpOptions,
): HexdumpLine {
  getHexdumpRowCount(input.length, options.bytesPerLine);
  const startOffset = rowIndex * options.bytesPerLine;
  if (!Number.isInteger(rowIndex) || rowIndex < 0 || startOffset >= input.length) {
    throw new Error('Hexdump row is outside the input');
  }
  const slice = input.subarray(startOffset, startOffset + options.bytesPerLine);
  const offset =
    options.offsetBase === 16
      ? startOffset.toString(16).padStart(8, '0')
      : startOffset.toString(10);
  const bytes = Array.from(slice, (value, index): HexdumpByte => {
    const hex = value.toString(16).padStart(2, '0');
    return {
      offset: startOffset + index,
      value,
      hex: options.uppercase ? hex.toUpperCase() : hex,
      ascii: value >= PRINTABLE_MIN && value <= PRINTABLE_MAX ? String.fromCharCode(value) : '.',
      colorGroup: getByteColorGroup(value),
    };
  });
  return { startOffset, offset, bytes };
}

export function formatHexdump(input: Uint8Array, options: HexdumpOptions): string {
  return formatHexdumpLines(input, options)
    .map((line) => {
      const hex = line.bytes.map((byte) => byte.hex).join(' ');
      const ascii = line.bytes.map((byte) => byte.ascii).join('');
      return `${line.offset}  ${hex.padEnd(options.bytesPerLine * 3 - 1, ' ')}  ${ascii}`;
    })
    .join('\n');
}
